import { Button, Checkbox, FormControl, FormControlLabel, FormLabel, Icon, IconButton, InputLabel, makeStyles, Menu, MenuItem, Select, Switch, Theme }
    from '@material-ui/core';
import { DatePickerView, KeyboardDatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { capitalizeAll, capitalizeFirst, SelectInputOption, TimeSpan, useDropdown, useScreenSizeCheck } from '@moderepo/mode-app-components';
import { UpdateMetricsReportSubscriptionParams } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import React, { CSSProperties, useCallback, useMemo } from 'react';
import { DayOfWeek } from '.';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectProject } from '../../utils/dataStateHooks';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'space-between',
            width         : '100%',
            flexWrap      : 'wrap',
        },

        leftControls: {
            flex          : 1,
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            width         : '100%',
            flexWrap      : 'wrap',
            '& $input'    : {
                marginRight: theme.spacing(1),
            },
        },

        rightControls: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-end',
            flexWrap      : 'wrap',
            '& $input'    : {
                marginLeft: theme.spacing(1),
            },
        },

        input: {
            '& .MuiOutlinedInput-input': {
                paddingTop   : theme.spacing(1.5),
                paddingBottom: theme.spacing(0.5),
            },
        },

        dateInputWrapper: {
            position             : 'relative',
            '&.has-remove-button': {
                marginRight: theme.spacing(2),
            },
        },

        dateInput: {
            width: '12em',
        },

        removeDateInput: {
            position : 'absolute',
            top      : 0,
            right    : theme.spacing(-1),
            transform: 'translateY(-50%)',
        },

        label: {
            textTransform: 'capitalize',
            fontWeight   : 'bold',
        },

        subscriptionMenuContent: {
            padding: theme.spacing(1),
        },

        subscriptionMenuSection: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'flex-start',
            justifyContent: 'center',
        },

        subscriptionMenuActions: {
            marginTop     : theme.spacing(1),
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-end',

            '& Button': {
                marginLeft: theme.spacing(2),
            },
        },

        [theme.breakpoints.up('md')]: {
            input: {
                marginRight: theme.spacing(2),
            },

            subscriptionMenuContent: {
                padding: theme.spacing(2),
            },
        },
    };
}, { name: 'EntityDetailsControlBar', index: 1 });



export interface EntityDetailsControlBarProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;

    // Input values provided by parent container. The parent container will be in control of the values. This component is a dumb
    // component and will not have any logic to do anything with these value other than display them.
    readonly timeSpan: TimeSpan;
    readonly timeSpanOptions: readonly SelectInputOption<TimeSpan>[];
    readonly timestamps: readonly number[];

    readonly showSubscriptionSettings: boolean;
    readonly subscriptionSettings: {
        readonly subscribed: boolean;

        // The options the user subscribed to. Only available if the user subscribed to the entity
        readonly subscribedToOptions?: {
            readonly receiveDailyReport?: boolean | undefined;
            readonly receiveWeeklyReport?: boolean | undefined;
            readonly receiveMonthlyReport?: boolean | undefined;
            readonly notifyOnlyOnAlert?: boolean | undefined;
        } | undefined
    };

    // The minimum # of timestamp inputs to show.
    readonly minTimestampInputs: number;

    // The maximum # of timestamp inputs to show.
    readonly maxTimestampInputs: number;

    // To specify the first day of the week. This is used when the timeSpan is in WEEK. If a value is set then we will only enable
    // the day specified. For example, if the value is 0 for SUNDAY then we will only enable sundays. Undefined value mean to let the user
    // choose any day
    readonly firstDayOfWeek?: DayOfWeek | undefined;

    // Whether or not to optimize the date selector UI. For example, when timeSpan is MONTH, sometime we only want the user to select
    // a year and month but not specific day. This flag can enable/disable that option. Same for when timeSpan is YEAR. The default is FALSE,
    // we will not disable day option and the user is allowed to select any day
    readonly optimizeDateSelector: boolean;

    readonly enablePrevButton?: boolean | undefined;
    readonly enableNextButton?: boolean | undefined;

    readonly onTimeSpanChanged: (timeSpan: TimeSpan)=> void;
    readonly onTimestampChanged: (timestamp: MaterialUiPickersDate, index: number)=> void;
    readonly onAddTimestamp: ()=> void;
    readonly onRemoveTimestamp: ()=> void;
    readonly onPrevButtonClicked?: ()=> void | undefined;
    readonly onNextButtonClicked?: ()=> void | undefined;

    readonly onSubscriptionToggle: (value: boolean)=> void;
    readonly onSubscriptionOptionToggle: (optionName: keyof UpdateMetricsReportSubscriptionParams, value: boolean)=> void;
    readonly onCancelSubscriptionChanges: ()=> void;
    readonly onSaveSubscriptionChanges: ()=> void;
}


export const EntityDetailsControlBar: React.FC<EntityDetailsControlBarProps> = (props: EntityDetailsControlBarProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const [subscriptionMenuAnchorEl, isSubscriptionMenuOpened, onSubscriptionMenuAnchorElClicked, onSubscriptionMenuClosed] = useDropdown();
    const isTabletOrLarger = useScreenSizeCheck('md');


    /**
     * The DateTimePicker calendar UI view can be configured to show only option to select Year or only option to select year and month.
     * So if the useFirstDayOfMonth is true, we will use these ['year', 'month'] view option so that the user can't select a date.
     * And if useFirstDayOfYear is true then we will only show the option to select a year. Else, we show the full calendar.
     * As for the Keyboard input options, we also need to set the input format to match the view. If the views is YEAR only then the
     * input format need to be 'YYYY'. If the views is YEAR and MONTH then the format needs to be 'YYYY/MM', etc...
     */
    const { views: calendarViews, dateFormat: keyboardInputDateFormat } = useMemo((): {
        views: DatePickerView[] | undefined;
        dateFormat: string;
    } => {
        if (props.optimizeDateSelector) {
            // If 'optimizeDayMonthSelect' option is turned on, we will hide some input based on the timeSpan
            if (props.timeSpan === TimeSpan.YEAR) {
                return {
                    views     : ['year'],
                    dateFormat: 'YYYY',
                };
            }
            if (props.timeSpan === TimeSpan.MONTH) {
                return {
                    views     : ['year', 'month'],
                    dateFormat: 'YYYY/MM',
                };
            }
        }

        // This mean the user can select anything so no need to filter out the day select options
        return {
            views     : undefined,
            dateFormat: 'YYYY/MM/DD',
        };
    }, [props.optimizeDateSelector, props.timeSpan]);



    // Create the date/time input field, one for each item in timestamps
    const createCompareToInput = useCallback(() => {
        const inputs: React.ReactElement[] = [];

        for (let i = 0; i < props.timestamps.length; i += 1) {
            // Show remove timestamp button on the LAST timestamp input EXCEPT for the first one
            const showRemoveButton = i > 0 && i === props.timestamps.length - 1;

            inputs.push(
                <div
                    key={i}
                    className={clsx(classes.dateInputWrapper, showRemoveButton && 'has-remove-button')}
                >
                    <KeyboardDatePicker
                        className={clsx(classes.input, classes.dateInput)}
                        label={capitalizeAll(trans(i === 0 ? 'label.date' : 'label.compare_to'))}
                        inputVariant="outlined"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        allowKeyboardControl
                        value={props.timestamps[i]}
                        format={keyboardInputDateFormat}
                        todayLabel={capitalizeFirst(trans('label.today'))}
                        okLabel={capitalizeFirst(trans('label.ok'))}
                        cancelLabel={capitalizeFirst(trans('label.cancel'))}
                        invalidDateMessage={capitalizeFirst(trans('error.input.date.invalid'))}
                        minDateMessage=""
                        maxDateMessage=""
                        showTodayButton
                        disableFuture
                        views={calendarViews}
                        shouldDisableDate={props.timeSpan === TimeSpan.WEEK
                            // If selected timeSpan is 1 WEEK then use this function to disable days that are not first day of the week
                            ? (date: MaterialUiPickersDate) => {
                                // If the firstDayOfWeek is undefined we will allow the user to pick any day so we return false, DON'T disable
                                if (props.firstDayOfWeek === undefined) {
                                    return false;
                                }
                                // If there is a SPECIFIC day set as firstDayOfWeek then disable the other days that is NOT the same as firstDayOfWeek
                                return date?.toDate().getDay() !== props.firstDayOfWeek;
                            }
                            : undefined}
                        onChange={(date: MaterialUiPickersDate) => {
                            props.onTimestampChanged(date, i);
                        }}
                    />

                    {showRemoveButton && (
                        <IconButton
                            className={classes.removeDateInput}
                            size="small"
                            onClick={props.onRemoveTimestamp}
                        >
                            <Icon fontSize="medium">cancel</Icon>
                        </IconButton>
                    )}
                </div>,
            );
        }
        return inputs;
    }, [props, classes.dateInputWrapper, classes.input, classes.dateInput, classes.removeDateInput, trans, keyboardInputDateFormat, calendarViews]);


    /**
     * On subscription toggler changed, call the handler and pass it the new value
     */
    const onSubscriptionTogglerChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        props.onSubscriptionToggle(event.target.checked);
    }, [props]);



    /**
     * On subscription OPTION checkbox checked/unchecked, call the handler and pass it the new value
     */
    const onSubscriptionOptionTogglerChange = useCallback((
        event: React.ChangeEvent<HTMLInputElement>, option: keyof UpdateMetricsReportSubscriptionParams,
    ) => {
        props.onSubscriptionOptionToggle(option, event.target.checked);
    }, [props]);



    // Because we allow user to use Keyboard to set the start/end time therefore it is possible for the user to enter the wrong
    // date/time value. So we must make sure all the timestamp inputs are valid
    const isTimestampsValid = useMemo(() => {
        // find a timestamp that is invalid
        return props.timestamps.find((timestamp) => {
            return !timestamp || Number.isNaN(timestamp.valueOf());
        }) === undefined;
    }, [props.timestamps]);


    return (
        <div className={clsx(props.className, classes.root)}>
            <div className={classes.leftControls}>
                <FormControl
                    variant="outlined"
                    fullWidth={false}
                    className={classes.input}
                >
                    <InputLabel shrink>{capitalizeAll(trans('label.time_span'))}</InputLabel>
                    <Select
                        label={capitalizeAll(trans('label.time_span'))}
                        value={props.timeSpan}
                        displayEmpty
                        onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                            const timeSpan = event.target.value as TimeSpan;
                            props.onTimeSpanChanged(timeSpan);
                        }}
                    >
                        {props.timeSpanOptions.map((option) => {
                            return (
                                <MenuItem key={option.label} value={option.value} disabled={option.disabled}>{option.label}</MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>

                {createCompareToInput()}


                {/**
                 * Show the "Compare To" button if we have not reached the maxTimestampInputs
                 */}
                {props.timestamps.length < props.maxTimestampInputs && (
                    <>
                        {isTabletOrLarger ? (
                            <Button
                                className={clsx(classes.input)}
                                variant="contained"
                                color="default"
                                onClick={props.onAddTimestamp}
                            >
                                {capitalizeAll(trans('label.compare_to'))}
                            </Button>
                        ) : (
                            <IconButton
                                onClick={props.onAddTimestamp}
                            >
                                <Icon>add</Icon>
                            </IconButton>
                        )}
                    </>
                )}

                {isTabletOrLarger ? (
                    <Button
                        className={clsx(classes.input, classes.button)}
                        variant="contained"
                        color="default"
                        disabled={!props.enablePrevButton || !isTimestampsValid}
                        onClick={props.onPrevButtonClicked}
                    >
                        {capitalizeAll(trans('label.prev'))}
                    </Button>
                ) : (
                    <IconButton
                        disabled={!props.enablePrevButton || !isTimestampsValid}
                        onClick={props.onPrevButtonClicked}
                    >
                        <Icon>arrow_back</Icon>
                    </IconButton>
                )}

                {isTabletOrLarger ? (
                    <Button
                        className={clsx(classes.input)}
                        variant="contained"
                        color="default"
                        disabled={!props.enableNextButton || !isTimestampsValid}
                        onClick={props.onNextButtonClicked}
                    >
                        {capitalizeAll(trans('label.next'))}
                    </Button>
                ) : (
                    <IconButton
                        disabled={!props.enableNextButton || !isTimestampsValid}
                        onClick={props.onNextButtonClicked}
                    >
                        <Icon>arrow_forward</Icon>
                    </IconButton>
                )}
            </div>

            {props.showSubscriptionSettings && (
                <div className={classes.rightControls}>
                    {isTabletOrLarger ? (
                        <div className={classes.input}>
                            <FormLabel className={classes.label}>{trans('entity.not_subscribed.label')}</FormLabel>
                            <Switch
                                checked={props.subscriptionSettings.subscribed}
                                onChange={onSubscriptionTogglerChange}
                            />
                            <FormLabel className={classes.label}>{trans('entity.subscribed.label')}</FormLabel>
                        </div>
                    ) : (
                        <FormControlLabel
                            className={clsx(classes.input, classes.label)}
                            label={trans('entity.subscribed.label')}
                            labelPlacement="start"
                            control={(
                                <Checkbox
                                    checked={props.subscriptionSettings.subscribed}
                                    onChange={onSubscriptionTogglerChange}
                                />
                            )}
                        />
                    )}

                    {props.subscriptionSettings.subscribed && (
                        // Only show the "Settings" button if the user subscribed to the entity
                        <IconButton size="small" onClick={onSubscriptionMenuAnchorElClicked}>
                            <Icon>settings</Icon>
                        </IconButton>
                    )}
                    <Menu
                        getContentAnchorEl={null}
                        anchorEl={subscriptionMenuAnchorEl}
                        anchorOrigin={{
                            vertical  : 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical  : 'top',
                            horizontal: 'right',
                        }}
                        PaperProps={{
                            className: classes.menu,
                        }}
                        keepMounted
                        open={isSubscriptionMenuOpened}
                        onClose={onSubscriptionMenuClosed}
                    >
                        <div className={classes.subscriptionMenuContent}>
                            <FormLabel className={classes.label}>{trans('entity.subscribe_to.label')}</FormLabel>
                            <div className={classes.subscriptionMenuSection}>
                                <FormControlLabel
                                    className={classes.label}
                                    label={trans('entity.input.daily_report.label')}
                                    control={(
                                        <Checkbox
                                            checked={props.subscriptionSettings.subscribedToOptions?.receiveDailyReport ?? false}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                onSubscriptionOptionTogglerChange(event, 'receiveDailyReport');
                                            }}
                                        />
                                    )}
                                />
                                <FormControlLabel
                                    className={classes.label}
                                    label={trans('entity.input.weekly_report.label')}
                                    control={(
                                        <Checkbox
                                            checked={props.subscriptionSettings.subscribedToOptions?.receiveWeeklyReport ?? false}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                onSubscriptionOptionTogglerChange(event, 'receiveWeeklyReport');
                                            }}
                                        />
                                    )}
                                />
                                <FormControlLabel
                                    className={classes.label}
                                    label={trans('entity.input.monthly_report.label')}
                                    control={(
                                        <Checkbox
                                            checked={props.subscriptionSettings.subscribedToOptions?.receiveMonthlyReport ?? false}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                onSubscriptionOptionTogglerChange(event, 'receiveMonthlyReport');
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <hr />
                            <FormLabel className={classes.label}>{trans('entity.options.label')}</FormLabel>
                            <div className={classes.subscriptionMenuSection}>
                                <FormControlLabel
                                    className={classes.label}
                                    label={trans('entity.input.notify_only_on_alert.label')}
                                    control={(
                                        <Checkbox
                                            checked={props.subscriptionSettings.subscribedToOptions?.notifyOnlyOnAlert ?? false}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                onSubscriptionOptionTogglerChange(event, 'notifyOnlyOnAlert');
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <div className={classes.subscriptionMenuActions}>
                                <Button
                                    onClick={() => {
                                        props.onCancelSubscriptionChanges();
                                        onSubscriptionMenuClosed();
                                    }}
                                >
                                    {trans('label.cancel')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        props.onSaveSubscriptionChanges();
                                        onSubscriptionMenuClosed();
                                    }}
                                >
                                    {trans('label.save')}
                                </Button>
                            </div>
                        </div>
                    </Menu>
                </div>
            )}
        </div>
    );
};
